<template>
  <wizard
    first-step="personalDetails"
    class="wizard-newborn-child"
  >
    <wizard-step
      :i18n="i18n"
      name="personalDetails"
      :model="formData"
      :next-step="submitPersonalDetails"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.personalDetails.title }}</h2>
        <slot name="personal-details-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <FormulateInput
          name="bonusCode"
          type="text"
          label="bonus code"
          validation="nohtml"
          class="custom-form-element"
          aria-hidden="true"
        />
        <FormulateInput
          name="website"
          type="text"
          label="website"
          validation="nohtml"
          class="custom-form-element"
          aria-hidden="true"
        />
        <FormulateInput
          :label="i18n.form.labels.name"
          name="name"
          validation="bail|required:trim|nohtml"
          :validation-messages="i18n.form.validations.name"
        />

        <FormulateInput
          name="email"
          type="email"
          :label="i18n.form.labels.email"
          validation="bail|required:trim|email"
          :validation-messages="i18n.form.validations.email"
        />

        <FormulateInput
          name="phoneNumber"
          type="text"
          :label="i18n.form.labels.phoneNumber"
          validation="bail|required:trim|nohtml"
          :validation-messages="i18n.form.validations.phoneNumber"
        />

        <FormulateInput
          type="text"
          name="debtorNumber"
          :label="i18n.form.labels.debtorNumber"
          :help="i18n.form.help.debtorNumber"
          validation="bail|required:trim|max:9,length|min:6,length|nohtml|matches:/^[0-9\-]+$/"
          validation-name="Klantnummer"
          :validation-messages="i18n.form.validations.debtorNumber"
          @keydown.space.prevent
        />
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="childDetails"
      :model="formData"
      next-step="review"
      previous-step="personalDetails"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.childDetails.title }}</h2>
        <slot name="child-details-paragraph" />
      </wizard-step-intro>

      <fieldset>
        <h3>{{ i18n.form.labels.childRegistrationItems }}</h3>

        <FormulateInput
          type="group"
          name="children"
          :add-label="i18n.form.addlabel.childDetails"
          :repeatable="true"
          validation="bail|min:1,length|max:5,length|unique:socialSecurityNumber"
          :validation-messages="i18n.form.validations.childDetails"
        >
          <div class="row">
            <!-- workaround for losing focus on first input -->
            <FormulateInput
              type="hidden"
              value="none"
            />
            <!-- /workaround -->

            <FormulateInput
              type="text"
              name="childInitials"
              :label="i18n.form.labels.childInitials"
              validation="bail|required:trim|nohtml|matches:/^[A-Z](\.[A-Z])*\.?$/"
              :validation-messages="i18n.form.validations.childInitials"
            />

            <FormulateInput
              type="text"
              name="childName"
              :label="i18n.form.labels.childFirstName"
              validation="bail|required:trim|nohtml"
              :validation-messages="i18n.form.validations.childFirstName"
            />

            <fields-date-picker
              field-name="dateOfBirth"
              :max-date="maxDateForDateOfBirth ? maxDateForDateOfBirth : ''"
              :date-validation="validationRulesForDateOfBirth"
              :i18n="i18n"
            />

            <fields-gender
              :i18n="i18n"
              :only-allow-known-options="true"
            />

            <FormulateInput
              type="text"
              name="socialSecurityNumber"
              :label="i18n.form.labels.socialSecurityNumber"
              validation="bail|required:trim|nohtml"
              :validation-messages="i18n.form.validations.socialSecurityNumber"
            />
          </div>
        </FormulateInput>
      </fieldset>
    </wizard-step>

    <wizard-step
      :i18n="i18n"
      name="review"
      :model="formData"
      :next-step="sendRequest"
      previous-step="childDetails"
      :submit-button-text="i18n.form.buttons.submit"
      :is-submitting="request.isSending"
      :error-submitting="request.errorSending"
    >
      <wizard-step-intro>
        <h2>{{ i18n.wizardSteps.review.title }}</h2>
        <slot name="review-paragraph" />
      </wizard-step-intro>

      <table class="wizard-summary">
        <tbody>
          <tr>
            <th>{{ i18n.form.labels.reviewName }}</th>
            <td>{{ resultingData.personalDetails.name }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewEmail }}</th>
            <td>{{ resultingData.personalDetails.email }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewPhoneNumber }}</th>
            <td>{{ resultingData.personalDetails.phoneNumber }}</td>
          </tr>
          <tr>
            <th>{{ i18n.form.labels.reviewCustomerNumber }}</th>
            <td>{{ resultingData.personalDetails.debtorNumber }}</td>
          </tr>

          <tr>
            <td colspan="2">
              {{ '\xa0' }}
            </td>
          </tr>

          <template v-for="(child, childIndex) of resultingData.changesPerChild">
            <tr :key="childIndex + '-name'">
              <td colspan="2">
                <h3>{{ child.childName }}</h3>
              </td>
            </tr>
            <tr :key="childIndex + '-initials'">
              <th>{{ i18n.form.labels.childInitials }}</th>
              <td>{{ child.childInitials }}</td>
            </tr>
            <tr :key="childIndex + '-gender'">
              <th>{{ i18n.form.labels.reviewGender }}</th>
              <td>{{ i18n.form.labels.genderoptions[child.gender] }}</td>
            </tr>
            <tr :key="childIndex + '-date-of-birth'">
              <th>{{ i18n.form.labels.dateOfBirth }}</th>
              <td>{{ moment(child.dateOfBirth).format('DD-MM-YYYY') }}</td>
            </tr>

            <tr :key="childIndex + '-social-security-number'">
              <th>{{ i18n.form.labels.socialSecurityNumber }}</th>
              <td>{{ child.socialSecurityNumber }}</td>
            </tr>

            <tr
              v-if="childIndex < resultingData.changesPerChild.length - 1"
              :key="childIndex + '-child-details-line-break'"
            >
              <td colspan="2">
                {{ '\xa0' }}
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </wizard-step>
  </wizard>
</template>

<script>
  import FieldsDatePicker from '@/components/forms/fields/FieldsDatePicker.vue';
  import FieldsGender from '@/components/forms/fields/FieldsGender.vue';
  import Wizard from '@/components/wizards/components/Wizard.vue';
  import WizardStep from '@/components/wizards/components/WizardStep.vue';
  import WizardStepIntro from '@/components/wizards/components/WizardStepIntro.vue';
  import getQueryParams from '@/lib/formulate/getFormQueryParam';
  import Utils from '@/lib/utils';
  import axios from 'axios';
  import moment from 'moment';

  export default {
    name: 'KfWizardNewbornChild',
    components: {
      FieldsDatePicker,
      Wizard,
      WizardStep,
      WizardStepIntro,
      FieldsGender
    },
    props: {
      i18n: {
        type: Object,
        default: null,
      },
      prefillData: {
        type: Object,
        default: () => { },
      },
      locale: {
        type: String,
        default: () => 'nl',
      },
      label: {
        type: String,
        default: 'Partou',
      },
      successPageUrl: {
        type: String,
        default: () => '/',
      },
      formToken: {
        type: String,
        default: () => '',
      },
    },
    data() {
      return {
        formData: {
          personalDetails: this.$store.state.wizard.personalDetails,
          childDetails: {
            children: [],
          },
        },
        request: {
          isSending: false,
          errorSending: false,
        },
      };
    },
    computed: {
      moment() {
        return moment;
      },
      maxDateForDateOfBirth() {
        return moment().format('YYYY-MM-DD');
      },
      validationRulesForDateOfBirth() {
        return `bail|required|before:${Utils.date.getDate(new Date(this.maxDateForDateOfBirth), 1)}`;
      },
      resultingData() {
        return {
          label: this.label,
          locale: this.locale,
          formToken: this.formToken,
          personalDetails: this.formData.personalDetails,
          changesPerChild: this.formData.childDetails.children,
        };
      },
    },
    created() {
      this.formData.personalDetails = {
        ...this.formData.personalDetails,
        ...getQueryParams(this.prefillData)
      }
    },
    methods: {
      translateDataType(dataType) {
        return this.i18n.form.labels.requestDataTypeOptions[dataType];
      },
      submitPersonalDetails() {
        this.$store.dispatch('wizard/setPersonalDetails', this.formData.personalDetails);
        return 'childDetails';
      },
      sendRequest() {
        this.request.errorSending = false;
        this.request.isSending = true;

        const requestVerificationToken = document.body.querySelector('input[type=hidden][name=__RequestVerificationToken]').value;

        const config = {
          headers: {
            'X-REQUEST-VERIFICATION-TOKEN': requestVerificationToken,
          }
        };

        axios
          .post('/umbraco/api/wizard/newbornchild', this.resultingData, config)
          .then(() => {
            window.location.href = this.successPageUrl;
          })
          .catch(() => {
            this.request.isSending = false;
            this.request.errorSending = true;
          });
      },
    },
  };
</script>
